import { useCallback, useEffect, useReducer, useRef } from 'react'
import { AuthError, EventType, AuthenticationResult, InteractionRequiredAuthError, EventCallbackFunction } from '@azure/msal-browser'
import { msalInstance } from '@/dataImplementation/auth'
import { useLocation } from 'react-router-dom'
import { PATHS, virginRedPath } from '@/router/paths'
import { getVaaGQLClient } from '../api/utils/vaaGQLClient'

type State = {
  data: AuthenticationResult | null
  isLoading: boolean
  error: AuthError | null
}

type Action =
  | {
      type: 'success'
      payload: AuthenticationResult | null
    }
  | {
      type: 'error'
      payload: AuthError
    }
  | {
      type: 'request'
    }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'success':
      return { ...state, data: action.payload, isLoading: false, error: null }
    case 'error':
      return { ...state, data: null, isLoading: false, error: action.payload }
    case 'request':
      return { ...state, isLoading: true, error: null }
    default:
      return state
  }
}

export const VAA_AUTH_SCOPES = ['openid']

type UseVaaAuthOptions = {
  eventCallback?: EventCallbackFunction
}

export const useVaaAuth = ({ eventCallback }: UseVaaAuthOptions = {}) => {
  const { hash } = useLocation()
  const initializedRef = useRef(false)
  const [state, dispatch] = useReducer(reducer, {
    data: null,
    // if there is a hash, then we know they are in the process of logging in
    isLoading: !!hash,
    error: null,
  })

  useEffect(() => {
    const callbackId = msalInstance.addEventCallback((message) => {
      switch (message.eventType) {
        case EventType.LOGIN_SUCCESS:
        case EventType.ACQUIRE_TOKEN_SUCCESS: {
          const payload = message.payload as AuthenticationResult

          if (payload.idToken) {
            getVaaGQLClient().setHeader('Authorization', `Bearer ${payload.idToken}`)
          }

          dispatch({ type: 'success', payload })
          break
        }
        case EventType.LOGIN_FAILURE:
        case EventType.ACQUIRE_TOKEN_FAILURE:
          dispatch({ type: 'error', payload: message.error as AuthError })
          break
      }
    })
    const externalCallbackId = eventCallback ? msalInstance.addEventCallback(eventCallback) : null

    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId)
      if (externalCallbackId) msalInstance.removeEventCallback(externalCallbackId)
    }
  }, [eventCallback])

  useEffect(() => {
    // should only run once - in development it runs multiple times
    if (!initializedRef.current) {
      initializedRef.current = true
      // responses/errors of msal instance function calls will be handled by the event callback
      msalInstance.handleRedirectPromise().catch()
    }
  }, [])

  useEffect(() => {
    const accounts = msalInstance.getAllAccounts()
    if (!accounts.length) return

    dispatch({ type: 'request' })

    const request = { account: accounts[0], scopes: VAA_AUTH_SCOPES }

    msalInstance.acquireTokenSilent(request).catch((err) => {
      if (err instanceof InteractionRequiredAuthError) {
        return msalInstance.acquireTokenRedirect(request)
      }
      return err
    })
  }, [])

  const login = useCallback(() => {
    dispatch({ type: 'request' })
    msalInstance.loginRedirect({ scopes: VAA_AUTH_SCOPES, prompt: 'login', redirectUri: `${virginRedPath}${PATHS.REDSKY_SEARCH}` })
  }, [])

  return { login, ...state }
}
